import { useEffect, useState } from 'react';

/**
 * Where the catalog stops having room for a filter column beside the results.
 *
 * One number, because the breakpoint is read twice — the hook asks the browser
 * and the stylesheet hides the toggle — and two copies of a breakpoint drift
 * into a width where neither layout is shown.
 */
export const NARROW_MAX_WIDTH_PX = 720;

export const NARROW_QUERY = `(max-width: ${NARROW_MAX_WIDTH_PX}px)`;

/**
 * Whether the viewport is narrow enough to need the collapsed layout.
 *
 * A viewport we cannot measure counts as wide. That is the layout that shows
 * every control at once; guessing narrow would put the filters behind a
 * disclosure that, if the guess was wrong, nothing ever tells the person is
 * there. `matchMedia` is missing in jsdom and in any pre-render, so this is
 * the normal path, not an edge case.
 */
export function useIsNarrow(): boolean {
  const [narrow, setNarrow] = useState(false);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia(NARROW_QUERY);
    setNarrow(query.matches);

    const onChange = (event: MediaQueryListEvent) => setNarrow(event.matches);
    // Safari before 14 has only the deprecated form, and the catalog is the
    // screen most likely to be opened on an old phone.
    if (typeof query.addEventListener === 'function') {
      query.addEventListener('change', onChange);
      return () => query.removeEventListener('change', onChange);
    }
    query.addListener(onChange);
    return () => query.removeListener(onChange);
  }, []);

  return narrow;
}
